// ============================================================================
// 1. 임포트 및 전역 설정
// ============================================================================
import React, { useState, useEffect } from 'react';
import { User, Deal, Contact } from '../types';
import { 
  FileSearch, FileText, Sparkles, Send, Loader2, 
  Briefcase, Target, Server, Wifi, WifiOff, RefreshCw,
  FolderOpen
} from 'lucide-react';

// 백엔드 연동 서비스
import { generateDocDraft } from '../services/iegAgentService';
import { crmDataService } from '../services/crmDataService';

interface SalesSupportProps {
  user: User;
  deals: Deal[];    
  contacts: Contact[];
  globalRefreshKey?: number;
}

type SupportTab = 'specification' | 'proposal';

const SalesSupport: React.FC<SalesSupportProps> = ({ user, deals, contacts, globalRefreshKey = 0 }) => {
  // ============================================================================
  // 2. 상태 관리
  // ============================================================================
  const [activeTab, setActiveTab] = useState<SupportTab>('specification');
  const [localDeals, setLocalDeals] = useState<any[]>([]); 
  const [selectedDealId, setSelectedDealId] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [dbStatus, setDbStatus] = useState<'checking' | 'connected' | 'disconnected'>('checking');
  const [aiPrompt, setAiPrompt] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');

  // ============================================================================
  // 3. 데이터 로딩 (실시간 목록 갱신)
  // ============================================================================
  
  const loadInitialData = async () => {
    setIsSyncing(true);
    try {
      const healthRes = await fetch('http://localhost:8000/api/admin/health');
      setDbStatus(healthRes.ok ? 'connected' : 'disconnected');

      // 📡 서버에서 최신 목록과 활동 로그를 합쳐서 가져옵니다.
      const fetched = await crmDataService.getDeals();
      setLocalDeals(fetched || []);
    } catch (error) {
      setDbStatus('disconnected');
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [globalRefreshKey]); // globalRefreshKey가 변경될 때마다 데이터 새로고침

  // ============================================================================
  // 4. 이벤트 핸들러 (상세 조회 및 영업 단계/금액 복구)
  // ============================================================================

  const handleDealSelect = async (id: string) => {
    setSelectedDealId(id);
    if (!id) {
      setAiPrompt('');
      return;
    }

    setIsProcessing(true);
    try {
      // 📡 선택 시점에 서버 DB 직접 조회 (캐시 무시)
      const response = await fetch(`http://localhost:8000/api/deals/${id}?t=${new Date().getTime()}`);
      if (!response.ok) throw new Error("데이터 조회 실패");
      
      const realDeal = await response.json();
      // 💡 탭 상태에 따라 문서 명칭을 즉시 결정
      const docName = activeTab === 'specification' ? '기술 규격서' : '사업 제안서';

      // 💡 실제 DB의 단계를 가져옴 (탭 이름과 혼동되지 않도록 변수명 명확히)
      const currentSalesStage = realDeal['영업리드'] || realDeal['activity_type'] || '미방문';
      
      // 💡 [금액 복구] DB의 소계가 0이면 제품+상품 금액 합산
      const prodAmt = Number(realDeal['제품금액'] || 0);
      const itemAmt = Number(realDeal['상품금액'] || 0);
      const totalAmt = Number(realDeal['소계'] || 0);
      const finalValue = (totalAmt === 0 && (prodAmt + itemAmt) > 0) ? (prodAmt + itemAmt) : totalAmt;

      

      const companyName = realDeal['거래처'] || '미지정';
      const title = (realDeal['건명'] && realDeal['건명'] !== '-') ? realDeal['건명'] : `${companyName} 프로젝트`;

      const promptText = 
`[작성 대상 정보]
- 고객사: ${companyName}
- 프로젝트명: ${title}
- 예산 규모: ₩${finalValue.toLocaleString()}
- 현재 영업 단계: ${currentSalesStage}
- 영업 활동 메모: ${realDeal['memo'] || "특이사항 없음"}

[요청 사항]
위 실시간 영업 현황을 바탕으로 우리 지식 DB에서 최적의 내용을 찾아 ${docName} 초안을 작성해줘.`;

      setAiPrompt(promptText);
    } catch (error) {
      alert("⚠️ 상세 데이터를 가져오지 못했습니다.");
    } finally {
      setIsProcessing(false);
    }
  };

  // 💡 [추가] 탭을 바꿀 때마다 프롬프트의 '문서 명칭'을 자동으로 갈아끼워주는 효과
useEffect(() => {
  if (selectedDealId) {
    handleDealSelect(selectedDealId);
  }
}, [activeTab]); // activeTab이 바뀔 때마다 실행됨


  const handleGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setIsProcessing(true);
    setGeneratedContent('');
    try {
      // 💡 [중요 수정] activeTab('specification' 또는 'proposal')을 직접 넘겨줍니다.
      // 이렇게 해야 백엔드 main.py의 PROMPT_TEMPLATES에서 맞는 파일을 읽어옵니다.
      const content = await generateDocDraft(activeTab, aiPrompt, user.name);
      setGeneratedContent(content);
    } catch (error) {
      console.error("문서 생성 에러:", error);
      setGeneratedContent("❌ 문서 생성 중 오류가 발생했습니다. 백엔드 연결을 확인해주세요.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 animate-fade-in pb-20">
      {/* 상단 컨트롤 */}
      <div className="flex justify-between items-center gap-4">
        <div className="bg-slate-200 p-1 rounded-2xl flex gap-1 border border-slate-300">
          <button 
            onClick={() => { setActiveTab('specification'); setGeneratedContent(''); setAiPrompt(''); setSelectedDealId(''); }}
            className={`px-6 py-2.5 rounded-xl text-sm font-black transition-all flex items-center gap-2 ${activeTab === 'specification' ? 'bg-white text-blue-600 shadow-lg' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <FileSearch size={18} /> 규격서 가공
          </button>
          <button 
            onClick={() => { setActiveTab('proposal'); setGeneratedContent(''); setAiPrompt(''); setSelectedDealId(''); }}
            className={`px-6 py-2.5 rounded-xl text-sm font-black transition-all flex items-center gap-2 ${activeTab === 'proposal' ? 'bg-white text-indigo-600 shadow-lg' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <FileText size={18} /> 제안서 가공
          </button>
        </div>
        <button onClick={loadInitialData} className="p-2.5 bg-white rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 shadow-sm transition-all">
          <RefreshCw size={20} className={isSyncing ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-280px)]">
        {/* 왼쪽 설정 패널 */}
        <div className="lg:col-span-1 flex flex-col gap-4 overflow-y-auto">
          <div className="bg-white p-5 rounded-[28px] shadow-sm border border-slate-200">
            <h3 className="text-xs font-black text-slate-400 uppercase mb-3 tracking-widest flex items-center gap-2">
              <Briefcase size={14} className="text-blue-500" /> STEP 1. 영업 대상 선택
            </h3>
            <select 
              value={selectedDealId}
              onChange={(e) => handleDealSelect(e.target.value)}
              className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 text-[12px] font-bold outline-none cursor-pointer"
            >
              <option value="">대상 프로젝트 선택</option>
              {localDeals.map((d: any) => {
                // 💡 리스트에서 [상태]와 금액을 모두 보여줌
                const stage = d['영업리드'] || '미방문';
                const val = Number(d['소계'] || d['제품금액'] || 0);
                return (
                  <option key={d.id} value={d.id}>
                    [{stage}] {d['거래처'] || '미지정'} - (₩{val.toLocaleString()})
                  </option>
                );
              })}
            </select>
          </div>

          <div className="bg-white p-5 rounded-[28px] shadow-sm border border-slate-200">
            <h3 className="text-xs font-black text-slate-400 uppercase mb-3 tracking-widest flex items-center gap-2">
              <Target size={14} className="text-amber-500" /> STEP 2. 지식 DB 연결 상태
            </h3>
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 flex items-center justify-between">
               <span className="text-[11px] font-bold text-slate-600 flex items-center gap-2"><Server size={14} className="text-slate-400"/> IEG Engine</span>
               {dbStatus === 'connected' ? (
                 <span className="text-[10px] font-black text-green-600 bg-green-100 px-2 py-0.5 rounded-full flex items-center gap-1"><Wifi size={10} /> Online</span>
               ) : (
                 <span className="text-[10px] font-black text-red-600 bg-red-100 px-2 py-0.5 rounded-full flex items-center gap-1"><WifiOff size={10} /> Offline</span>
               )}
            </div>
          </div>

          <div className="bg-white p-5 rounded-[28px] shadow-sm border border-slate-200 flex flex-col flex-1">
            <h3 className="text-xs font-black text-slate-400 uppercase mb-3 tracking-widest flex items-center gap-2">
              <Sparkles size={14} className="text-purple-500" /> STEP 3. AI 가공 명령
            </h3>
            <textarea 
              className="w-full flex-1 p-4 bg-slate-50 border border-slate-200 rounded-xl text-[11px] font-bold outline-none resize-none leading-relaxed"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="데이터 선택 시 자동 구성됩니다..."
            />
            <button 
              onClick={handleGenerate}
              disabled={isProcessing || !aiPrompt.trim()}
              className="w-full mt-4 bg-blue-600 text-white py-4 rounded-xl font-black text-sm flex items-center justify-center gap-2 hover:bg-blue-700 transition-all shadow-xl shadow-blue-100"
            >
              {isProcessing ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              문서 생성 시작
            </button>
          </div>
        </div>

        {/* 오른쪽 미리보기 패널 */}
        <div className="lg:col-span-2 bg-white rounded-[40px] shadow-2xl border border-slate-200 flex flex-col overflow-hidden min-h-[500px]">
          <div className="p-6 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <div className={`w-2.5 h-6 rounded-full ${activeTab === 'specification' ? 'bg-slate-900' : 'bg-blue-600'}`}></div>
                <h3 className="font-black text-slate-800">최종 가공 결과 미리보기</h3>
              </div>
            </div>
          </div>

          <div className="flex-1 p-12 overflow-y-auto bg-white">
            {isProcessing ? (
              <div className="h-full flex flex-col items-center justify-center gap-4">
                <div className="w-16 h-16 border-4 border-blue-50 border-t-blue-600 rounded-full animate-spin"></div>
                <p className="text-slate-700 font-black animate-pulse">AI가 문서를 작성 중입니다...</p>
              </div>
            ) : (
              <div className="whitespace-pre-wrap font-sans text-slate-700 leading-relaxed text-[13px] font-medium">
                {generatedContent || "왼쪽에서 영업 대상을 선택하고 생성을 눌러주세요."}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesSupport;